import React, { useRef, useState, useEffect } from "react";
import { db, storage } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { doc } from "firebase/firestore";
import TeamNameSash from "./components/TeamNameSash";


// Glow for the card border based on rank - SIMPLIFIED
const getGlow = (rank) => {
  // Use a neutral shadow for all cards, ignoring rank
  if (rank === 0) return "0 0 32px 8px #ffd700, 0 0 0 7px #fff8";
  if (rank === 1) return "0 0 32px 8px #c0c0c0, 0 0 0 7px #fff8";
  return "0 12px 64px #000b, 0 0 0 4px rgba(255,255,255,0.2)";
};

// Helper for '1st', '2nd', etc. - KEPT, but no longer used in rendering
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// questionscores.js - CLEANED FOR SINGLE QUESTION SCORING

function QuestionScoresBody({ questionIdx, onClose, gameCode, questions }) { 



  const [answers, setAnswers] = useState([]);
  const [teamInfo, setTeamInfo] = useState({});
  const [questionScores, setQuestionScores] = useState([]); 
  
  const scrollRef = useRef(null);
  const [rowWidth, setRowWidth] = useState(0);
  const [scrollReady, setScrollReady] = useState(false);

  // --- CRITICAL MODE DETECTION ---
  // If this file were used for ENDROUND, this would be true. Since it's only question scores, 
  // we default to false but keep the logic structure simple.
  const isCustomAnimation = false; 
  
  // Custom scroll state
  const [scrollAnimation, setScrollAnimation] = useState({ name: 'none', duration: '0s', translateX: '0px' });
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const advanceTimerRef = useRef(0);
  const rafIdRef = useRef(0);
  
const [animationTriggered, setAnimationTriggered] = useState(false);




  const question = Array.isArray(questions) ? questions[questionIdx] : null;
  const questionType = String(question?.type || question?.CAT || "").toUpperCase();
  const isNearest = questionType === "NEARESTANSWER";
  const [answeredFasterTeams, setAnsweredFasterTeams] = useState(new Set());

  // Determine mode and title (SIMPLIFIED)
  const titleText = isNearest ? "🚩 Nearest Answer Results" : "🚩 Scores for This Question";
  const correctValue = isNearest ? Number(question?.answers?.[0]) : null;
  const showCorrectCard = isNearest && Number.isFinite(correctValue);
  const correctValueText = showCorrectCard ? String(correctValue) : "";
  const correctValueDigits = correctValueText.replace(/[^0-9]/g, "").length;
  const correctValueFontSize =
    correctValueDigits >= 9 ? "clamp(2.2rem, 3.4vw, 4rem)"
    : correctValueDigits >= 7 ? "clamp(2.6rem, 3.8vw, 4.6rem)"
    : correctValueDigits >= 6 ? "clamp(3rem, 4.4vw, 5.2rem)"
    : correctValueDigits >= 5 ? "clamp(3.4rem, 5vw, 5.6rem)"
    : "clamp(3.8rem, 5.8vw, 6.2rem)";
  
  // Memoize the onClose callback to safely use it in effects
  const handleContinue = React.useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  // Ref to latest handleContinue so the scroll animation effect doesn't depend on it.
  // (Parent often passes inline onClose, so handleContinue changes every render and would
  // restart the animation repeatedly — e.g. after WHEEL questions.)
  const handleContinueRef = React.useRef(handleContinue);
  React.useEffect(() => {
    handleContinueRef.current = handleContinue;
  }, [handleContinue]);

  // Add a listener for the "ArrowRight" key to manually advance
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleContinue();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleContinue]);

  useEffect(() => {
    if (scrollRef.current) {
      // Marquee mode needs doubled width for measurement
      const fullSetWidth = scrollRef.current.scrollWidth / 2;
      setRowWidth(fullSetWidth);
    }
  }, [questionScores.length]);

  // Listen for answers
  useEffect(() => {
    if (!gameCode) return;
    const unsub = onSnapshot(
      collection(db, "games", gameCode, "answers"),
      (snap) => {
        const all = [];
        snap.forEach((doc) => {
          const data = doc.data();
          // Filter to only include answers for THIS question with points
          if (data.q === questionIdx && typeof data.points === "number") {
            all.push(data);
          }
        });
        setAnswers(all);
      }
    );
    return unsub;
  }, [gameCode, questionIdx]);

  // Listen for teams & their selfies
  useEffect(() => {
    if (!gameCode) return;
    const unsub = onSnapshot(
      collection(db, "games", gameCode, "teams"),
      (snap) => {
        const info = {};
        snap.forEach((doc) => {
          const data = doc.data();
          info[data.team] = data.selfieUrl || null;
        });
        setTeamInfo(info);
      }
    );
    return unsub;
  }, [gameCode]);
  
  // CORE SCORING LOGIC: Aggregate results for THIS question
  useEffect(() => {
      const scoreByTeam = {};
      const currentTeams = new Set(Object.keys(teamInfo));
      const correctValue = isNearest ? Number(question?.answers?.[0]) : null;
      const answerGroups = {};

      // Filter answers for the current question
      const relevantAnswers = answers.filter(a => a.q === questionIdx);
      
      // 2. Aggregate points + (nearest: guesses + diff)
      relevantAnswers.forEach((a) => {
          if (!currentTeams.has(a.team)) return;
          const points = a.points;
          if (typeof points !== "number" || Number.isNaN(points)) return;

          if (isNearest && Number.isFinite(correctValue)) {
            const guess = Number(a.answer);
            const submittedAtMs =
              (a.submittedAt && typeof a.submittedAt.toMillis === "function")
                ? a.submittedAt.toMillis()
                : (typeof a.submittedAt === "number"
                    ? a.submittedAt
                    : (typeof a.time === "number" ? a.time : Number.POSITIVE_INFINITY));
            const diff = Number.isFinite(guess) ? guess - correctValue : null;
            scoreByTeam[a.team] = {
              points,
              guess: Number.isFinite(guess) ? guess : null,
              diff,
              submittedAtMs
            };
            if (Number.isFinite(guess)) {
              const key = String(guess);
              if (!answerGroups[key]) answerGroups[key] = [];
              answerGroups[key].push({ team: a.team, points, submittedAtMs });
            }
          } else {
            scoreByTeam[a.team] = { points };
          }
      });

      // 3. Ensure all participating teams for THIS question are represented
      const scored = Object.entries(scoreByTeam)
          .filter(([, payload]) => payload && !isNaN(payload.points))
          .sort((a, b) => {
            if (!isNearest) {
              return b[1].points - a[1].points || a[0].localeCompare(b[0]);
            }
            const diffA = Number.isFinite(a[1].diff) ? Math.abs(a[1].diff) : Number.POSITIVE_INFINITY;
            const diffB = Number.isFinite(b[1].diff) ? Math.abs(b[1].diff) : Number.POSITIVE_INFINITY;
            if (diffA !== diffB) return diffA - diffB;
            const timeA = Number.isFinite(a[1].submittedAtMs) ? a[1].submittedAtMs : Number.POSITIVE_INFINITY;
            const timeB = Number.isFinite(b[1].submittedAtMs) ? b[1].submittedAtMs : Number.POSITIVE_INFINITY;
            if (timeA !== timeB) return timeA - timeB;
            return a[0].localeCompare(b[0]);
          })
          .map(([team, payload]) => ({
            team,
            points: payload.points,
            guess: payload.guess,
            diff: payload.diff,
            submittedAtMs: payload.submittedAtMs
          }));
          
      setQuestionScores(scored);

      if (isNearest) {
        const winners = new Set();
        Object.values(answerGroups).forEach((group) => {
          if (!Array.isArray(group) || group.length < 2) return;
          const top = group.reduce((best, entry) => {
            if (!best) return entry;
            const timeBest = Number.isFinite(best.submittedAtMs) ? best.submittedAtMs : Number.POSITIVE_INFINITY;
            const timeEntry = Number.isFinite(entry.submittedAtMs) ? entry.submittedAtMs : Number.POSITIVE_INFINITY;
            if (timeEntry !== timeBest) return timeEntry < timeBest ? entry : best;
            if (entry.points !== best.points) return entry.points > best.points ? entry : best;
            return best;
          }, null);
          if (top?.team) winners.add(top.team);
        });
        setAnsweredFasterTeams(winners);
      } else {
        setAnsweredFasterTeams(new Set());
      }

// --- Trigger animation ONCE when scores first appear ---
      if (scored.length > 0 && !animationTriggered) {
        console.log("QuestionScores: First scores calculated, triggering animation."); // Add log
        setAnimationTriggered(true);
      }
      // --- End Trigger ---

      
  }, [answers, teamInfo, questionIdx, isNearest, question]);

// --- NEW: CONSTANT SPEED, SINGLE-PASS SCROLL ---
 // --- Animation useEffect (No internal setTimeout) ---
  useEffect(() => {
    // --- Run only AFTER animation has been triggered ---
    if (!animationTriggered || !scrollRef.current) {
      console.log(`QuestionScores Animation: Skipping (Triggered: ${animationTriggered}, Ref: ${!!scrollRef.current})`);
      return;
    }
    if (questionScores.length === 0) {
        console.log("QuestionScores Animation: Skipping (Scores became empty after trigger)");
        return;
    }
    // --- End Check ---

    // Clear any previous timers/frames
    clearTimeout(advanceTimerRef.current);
    cancelAnimationFrame(rafIdRef.current);
    setScrollReady(false);

    const scrollEl = scrollRef.current;
    const containerEl = scrollEl.parentElement;
    console.log("QuestionScores Animation: Starting setup...");

    // Use rAF to measure after paint
    rafIdRef.current = requestAnimationFrame(() => {
        // Ensure elements still exist
        if (!containerEl || !scrollEl) { console.error("QuestionScores Anim (rAF): Missing elements"); return; }
        const containerWidth = containerEl.clientWidth;
        const cardEl = scrollEl.querySelector('.scroll-row > div:first-child');
        if (!cardEl) { console.error("QuestionScores Anim (rAF): Missing card element"); return; }

        // --- 1. Measure Widths ---
        const cardWidth = cardEl.clientWidth;
        console.log(`QuestionScores Animation (rAF): cardWidth=${cardWidth}`); // Log measured width
        if (cardWidth <= 0) {
            console.error("QuestionScores Animation: Measured cardWidth is invalid, aborting animation.");
            // Optionally try again slightly later if needed, but for now just abort
             try { window.__overlayActive = false; } catch {} // Release lock if aborting
            return;
        }
        const cardGap = 0.02 * containerWidth;
        const totalCards = questionScores.length + (showCorrectCard ? 1 : 0);
        const contentWidth = totalCards * (cardWidth + cardGap);
        console.log(`QuestionScores Animation: containerWidth=${containerWidth}, cardGap=${cardGap.toFixed(2)}, contentWidth=${contentWidth.toFixed(2)}`);

        // --- Guard: Content fits statically ---
         if (contentWidth <= containerWidth) {
            console.log(`QuestionScores Animation: Content fits, applying static transform.`);
            scrollEl.style.transform = 'translateX(0)';
            scrollEl.style.animation = 'none';
            setScrollReady(true);
            const VIEWING_DELAY_SECS_STATIC = 6;
            advanceTimerRef.current = setTimeout(() => handleContinueRef.current(), VIEWING_DELAY_SECS_STATIC * 1000);
            return;
          }

        // --- 2. Calculate Duration (Fixed) ---
        const totalAnimationDuration = 12.0;

        // --- 3. Define Keyframes ---
        const animationName = `single-pass-marquee-${gameCode}-${questionIdx}`;
        const styleId = `style-${animationName}`;
        const startTransform = "translateX(100vw)";
        const endTransform = `translateX(calc(${-(contentWidth - containerWidth + cardGap * 2).toFixed(2)}px))`;
        console.log(`QuestionScores Animation: start='${startTransform}', end='${endTransform}', duration=${totalAnimationDuration.toFixed(2)}s`);

        // Inject the keyframe definitions
        const styleTag = document.getElementById(styleId) || document.createElement("style");
        styleTag.id = styleId;
        styleTag.innerHTML = `@keyframes ${animationName} { 0% { transform: ${startTransform}; } 100% { transform: ${endTransform}; } }`;
        if (!document.getElementById(styleId)) { document.head.appendChild(styleTag); }

        // --- 4. Apply the Animation ---
        scrollEl.style.animation = `${animationName} ${totalAnimationDuration.toFixed(2)}s ease-out forwards`;
        setScrollReady(true);
        console.log(`QuestionScores Animation: Applied animation: ${scrollEl.style.animation}`);

        // --- 5. Set Auto-Advance Timer ---
        const VIEWING_DELAY_SECS = 3;
        advanceTimerRef.current = setTimeout(() => {
          handleContinueRef.current();
        }, (totalAnimationDuration + VIEWING_DELAY_SECS) * 1000);

    }); // End requestAnimationFrame callback

    // Cleanup function for the effect
    return () => {
      console.log("QuestionScores Animation: Cleaning up effect.");
      clearTimeout(advanceTimerRef.current);
      cancelAnimationFrame(rafIdRef.current);
      // Ensure style is cleared using the ref, guarding against it being null
      if (scrollRef.current) {
        scrollRef.current.style.animation = 'none';
      }
      setScrollReady(false);
      // Release overlay lock on cleanup
       try { window.__overlayActive = false; } catch {}
    };

  }, [animationTriggered, questionIdx, gameCode]); // Do NOT include handleContinue — use handleContinueRef to avoid restarting scroll on every parent re-render (WHEEL bug)



  // Display Scores (single pass only, do not double the array)
  // Always scroll left-to-right with lowest first, highest last.
  const scoresSorted = React.useMemo(() => {
    return [...questionScores].sort((a, b) => {
      if (isNearest) {
        const diffA = Number.isFinite(a?.diff) ? Math.abs(a.diff) : Number.POSITIVE_INFINITY;
        const diffB = Number.isFinite(b?.diff) ? Math.abs(b.diff) : Number.POSITIVE_INFINITY;
        if (diffA !== diffB) return diffA - diffB;
        const timeA = Number.isFinite(a?.submittedAtMs) ? a.submittedAtMs : Number.POSITIVE_INFINITY;
        const timeB = Number.isFinite(b?.submittedAtMs) ? b.submittedAtMs : Number.POSITIVE_INFINITY;
        if (timeA !== timeB) return timeA - timeB;
        return String(a?.team || "").localeCompare(String(b?.team || ""));
      }
      const aPts = Number.isFinite(a?.points) ? a.points : 0;
      const bPts = Number.isFinite(b?.points) ? b.points : 0;
      if (aPts !== bPts) return bPts - aPts;
      return String(a?.team || "").localeCompare(String(b?.team || ""));
    });
  }, [questionScores, isNearest]);
  const displayScores = React.useMemo(() => {
    return [...scoresSorted].reverse();
  }, [scoresSorted]);
  const rankByTeam = React.useMemo(() => {
    const map = new Map();
    let lastMetric = null;
    let lastRank = 0;
    scoresSorted.forEach((item, idx) => {
      const metric = isNearest
        ? (Number.isFinite(item?.diff) ? Math.abs(item.diff) : Number.POSITIVE_INFINITY)
        : (Number.isFinite(item?.points) ? item.points : 0);
      let rank = idx + 1; // 1 = best
      if (lastMetric !== null && metric === lastMetric) {
        rank = lastRank; // same metric = same rank
      }
      map.set(item.team, rank);
      lastMetric = metric;
      lastRank = rank;
    });
    return map;
  }, [scoresSorted, isNearest]);
  const tieByTeam = React.useMemo(() => {
    const counts = new Map();
    scoresSorted.forEach((item) => {
      const metric = isNearest
        ? (Number.isFinite(item?.diff) ? Math.abs(item.diff) : Number.POSITIVE_INFINITY)
        : (Number.isFinite(item?.points) ? item.points : 0);
      counts.set(metric, (counts.get(metric) || 0) + 1);
    });
    const tied = new Map();
    scoresSorted.forEach((item) => {
      const metric = isNearest
        ? (Number.isFinite(item?.diff) ? Math.abs(item.diff) : Number.POSITIVE_INFINITY)
        : (Number.isFinite(item?.points) ? item.points : 0);
      tied.set(item.team, (counts.get(metric) || 0) > 1);
    });
    return tied;
  }, [scoresSorted, isNearest]);

  const CARD_WIDTH = "18vw";
  const CARD_HEIGHT = "70vh";
  const CARD_GAP = "2vw";

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        minHeight: "100vh",
        background: "transparent",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "sans-serif",
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        zIndex: 99999,
        pointerEvents: "none"
      }}
    >
      {/* Heading */}
      <div
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
          border: "3px solid #fff",
          borderRadius: "1.2em",
          padding: "0.1em 0.1em",
          marginTop: 8,
          marginBottom: 8,
          width: "90%",
          maxWidth: 800,
          fontSize: "3rem",
          fontWeight: 700,
          color: "#fff",
          textAlign: "center",
          boxShadow: "0 2px 18px 0 #0008",
          pointerEvents: "auto"
        }}
      >
        {titleText}
      </div>

      {/* Marquee/Scroll Area */}
      <div
        style={{
          width: "100vw",
          flex: 1,
          height: "100%",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          paddingTop: "6vh",
          pointerEvents: "auto"
        }}
      >
        <div
          className="scroll-row"
          ref={scrollRef}
          style={{
            height: CARD_HEIGHT,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            gap: CARD_GAP,
            opacity: scrollReady ? 1 : 0,
            // Animation is applied only by the useEffect (injected keyframe + scrollRef.current.style.animation).
            // Do NOT set animation here or React will overwrite it on re-render and the scroll-in-from-right won't run.
            flexDirection: 'row', // Always left-to-right flow
          }}
        >
          {displayScores.map((a, idx) => {
            const selfieUrl = teamInfo[a.team];
            // rank: 0 is leader visually
            const rank = questionScores.length - 1 - (idx % questionScores.length);
            const place = rankByTeam.get(a.team) || idx + 1;
            const isTied = tieByTeam.get(a.team) || false;
            return (
              <div
                key={a.team + idx}
                style={{
                  height: "100%",
                  aspectRatio: "2/3",
                  width: CARD_WIDTH,
                  minWidth: CARD_WIDTH,
                  maxWidth: CARD_WIDTH,
                  borderRadius: "2.2em",
                  background: "rgba(0,0,0,0.80)",
                  // use neutral glow
                  boxShadow: getGlow(rank),
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {/* Place badge */}
                <div
                  style={{
                    position: "absolute",
                    top: -18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "linear-gradient(180deg, #fff7c2, #f2c94c)",
                    color: "#4b3b00",
                    borderRadius: "1.2em",
                    padding: "0.25em 1em",
                    fontWeight: 900,
                    fontSize: "1.4rem",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    zIndex: 5,
                  }}
                >
                  {ordinal(place)}{isTied ? "=" : ""}
                </div>

                <TeamNameSash
                  teamName={a.team}
                  place={place}
                  selfieUrl={selfieUrl}
                  showFasterBadge={
                    isNearest && answeredFasterTeams.has(a.team)
                  }
                  containerStyle={{
                    height: "63%",
                    flexShrink: 0,
                  }}
                />

                {isNearest && (
                  <div
                    style={{
                      width: "90%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                      marginBottom: 6,
                      color: "#e9f0ff",
                      textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                      fontWeight: 800,
                      fontSize: "1.6rem",
                      letterSpacing: 0.4,
                    }}
                  >
                    <div>
                      Guess:{" "}
                      <span style={{ color: "#fff" }}>
                        {Number.isFinite(a.guess) ? String(a.guess) : "--"}
                      </span>
                    </div>
                    <div>
                      Error:{" "}
                      <span style={{ color: "#ff3b3b" }}>
                        {Number.isFinite(a.diff)
                          ? a.diff === 0
                            ? "0"
                            : a.diff > 0
                              ? `+${Math.abs(a.diff)}`
                              : `-${Math.abs(a.diff)}`
                          : "--"}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "auto",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      minWidth: 120,
                      minHeight: 70,
                      background: "linear-gradient(90deg, #fffbe2 5%, #ffe066 80%)",
                      border: "5px solid #ffe066",
                      borderRadius: "1.8em",
                      boxShadow: "0 6px 24px #ffd700aa",
                      color: "#8e7600",
                      fontWeight: 900,
                      fontSize: "3.6rem",
                      textAlign: "center",
                      padding: "0.3em 1em",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {typeof a.points === "number" ? a.points : ""}
                  </div>
                </div>

              </div>
            );
          })}
          {showCorrectCard && (
            <div
              key="correct-answer-card"
              style={{
                height: "100%",
                aspectRatio: "2/3",
                width: CARD_WIDTH,
                minWidth: CARD_WIDTH,
                maxWidth: CARD_WIDTH,
                borderRadius: "2.2em",
                background: "linear-gradient(180deg, rgba(46,204,113,0.55), rgba(31,157,85,0.55))",
                boxShadow: "0 12px 64px #000b, 0 0 0 4px rgba(255,255,255,0.2)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                position: "relative",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -18,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "linear-gradient(180deg, #fff7c2, #f2c94c)",
                  color: "#4b3b00",
                  borderRadius: "1.2em",
                  padding: "0.25em 1em",
                  fontWeight: 900,
                  fontSize: "1.2rem",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Correct Answer
              </div>

              <div
                style={{
                  width: "100%",
                  height: "63%",
                  borderRadius: "2em 2em 1.1em 1.1em",
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #2ecc71, #1f9d55)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#083b1c",
                  textShadow: "0 4px 18px rgba(0,0,0,0.25)",
                }}
              >
                <div style={{
                  fontSize: correctValueFontSize,
                  fontWeight: 900,
                  color: "#eafff3",
                  lineHeight: 1,
                  maxWidth: "92%",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden"
                }}>
                  {correctValueText}
                </div>
                <div style={{ fontSize: "3.2rem", lineHeight: 1, marginTop: "0.8em" }}>
                  ✅
                </div>
              </div>

              <div style={{ marginTop: "auto", marginBottom: 10 }} />
            </div>
          )}
        </div>
      </div>

      <style>
        {`
        /* Standard Marquee definition */
        @keyframes marquee-scroll {
          0%   { transform: translateX(calc(-1 * var(--marquee-width))); }
          100% { transform: translateX(100vw); }
        }
        .scroll-row { will-change: transform; }
        @media (max-width: 1000px) {
          .scroll-row > div {
            width: 200px !important;
            min-width: 200px !important;
            max-width: 200px !important;
            height: 320px !important;
          }
          .scroll-row > div > div:first-child { height: 160px !important; }
          .scroll-row > div > div:nth-child(2),
          .scroll-row > div > div:nth-child(3) { font-size: 1.2rem !important; }
        }
        `}
      </style>
    </div>
  );
}


// ----- Overlay wrapper: makes QuestionScores behave like a popup without touching the background -----
export default function QuestionScores(props) {
  return <QuestionScoresBody {...props} />;
}
